
































































































































import Vue from "vue";
import ApiSearchPage from "@/components/apiSearch/components/ApiSearchPage.vue";
import IconButton from "@/components/IconButton.vue";
import SyncStatusBadge from "../SyncStatusBadge.vue";
import DateTimeDisplay from "@/components/DateTimeDisplay.vue";
import MoneyInput from "@/components/MoneyInput.vue";
import InvoiceInfo from "./InvoiceInfo.vue";
import { ColumnType } from "@/components/apiSearch/models/ApiSearch";
import { taxCodeAndName } from "../../models/IfaceSyncInvoiceItemDTO";
import { Iface } from "../../models/Iface";
import store, { InterfaceStore } from "../../services/InterfaceStore";
import configuration from "@/models/Configuration";
import { roundMoney } from "@/services/prices";
import eventBus from "@/services/eventBus";

interface Props {
  /** Az adott számlafej (mlsysisync_invhead.szam) — ehhez tartozó tételek kerülnek listázásra */
  headId: number;
  /** Csoportosító külső azonosító — csak megjelenítéshez a fejlécben */
  extCode: string;
  /** A szülő számlafej-rekord (opcionális). Ha meg van adva, tétel-szerkesztéskor a fejet is megjelöljük.
   *  Ezen tároljuk a `_cachedItems`-et is, hogy ne resetelődjön a tételek állapota csukás/nyitáskor. */
  head: any;
  /** Csak olvasható — ekkor nincsenek funkciógombok (pl. InvoiceHistory tételei). */
  readonly: boolean;
  /** A fejléc-összegző (invoice-info) megjelenjen-e a tétel lista felett. */
  showInfo: boolean;
}

interface Methods {
  saveItem(item: any): void;
  cacheItems(items: any[]): void;
  /** A tétel szerkesztő mezői aktívak-e (csak olvasható módban soha). */
  isItemInEdit(item: any): boolean;
  /** Áfakulcs megjelenítés: "(külső kód) K2D megnevezés". */
  taxDisplay(item: any): string;
  /** Az adott összeg mező számolt-e a tételen (ilyenkor nem szerkeszthető). */
  isCalculatedField(field: string, item: any): boolean;
  /** Tétel szintű összeg tizedesjegyek — egységárnál külön (finomabb) szabály érvényes. */
  moneyDigits(field: string): number;
  /** Számla pénznemében megadott összeg beírása — a HUF megfelelőt az árfolyammal frissítjük. */
  setCurrAmount(item: any, field: string, value: number): void;
}

interface Computed {
  url: string;
  apiSearchName: string;
  columns: any[];
  hasCachedItems: boolean;
}

interface Data {
  store: InterfaceStore;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "InvoiceDetails",

  components: {
    ApiSearchPage,
    IconButton,
    SyncStatusBadge,
    DateTimeDisplay,
    MoneyInput,
    InvoiceInfo,
  },

  props: {
    headId: { type: Number, required: true },
    extCode: { type: String, default: "" },
    head: { type: Object, default: null },
    readonly: { type: Boolean, default: false },
    showInfo: { type: Boolean, default: true },
  },

  data() {
    return { store };
  },

  computed: {
    url(): string {
      return `iface-sync-invoice-item/search/${this.headId}`;
    },

    apiSearchName(): string {
      return `InvoiceItemsApiSearch_${this.headId}`;
    },

    /** Van-e már cache-elt tétel lista a head-en — ekkor nem töltünk újra. */
    hasCachedItems(): boolean {
      return !!this.head && Array.isArray(this.head._cachedItems);
    },

    columns(): any[] {
      return [
        { field: "command", title: "", cell: "command", filterable: false, sortable: false, width: 90 },
        { field: "id", title: "Azonosító", width: 80, cell: "disabledText" },
        { field: "itemName", title: "Megnevezés", cell: "editableText" },
        { field: "parts", title: "Cikk azonosító", width: 130, cell: "editableText" },
        { field: "gparts", title: "Egyéb cikk azonosító", width: 130, cell: "editableText" },
        { field: "quantity", title: "Mennyiség", width: 90, type: ColumnType.Right, cell: "editableNumber", filterable: false },
        { field: "unitType", title: "Egység", width: 80, cell: "editableText" },
        { field: "currUnitPrice", title: "Egységár", width: 110, type: ColumnType.Money, cell: "editableMoney", filterable: false },
        { field: "currNetto", title: "Nettó", width: 110, type: ColumnType.Money, cell: "editableMoney", filterable: false },
        { field: "currVat", title: "Áfa", width: 110, type: ColumnType.Money, cell: "editableMoney", filterable: false },
        { field: "currTotal", title: "Bruttó", width: 110, type: ColumnType.Money, cell: "editableMoney", filterable: false },
        { field: "tax", title: "Áfa kód", width: 190, cell: "taxCode" },
        { field: "syncRemark", title: "Megjegyzés", width: 200, cell: "editableText" },
        { field: "errorText", title: "Hibaüzenet", width: 220, fieldClass:"error-text-col", cell: "disabledText" },
        { field: "syncStatusName", title: "Státusz", width: 140, cell: "syncStatusName" },
        { field: "updUserName", title: "Utoljára módosította", width: 130, cell: "disabledText" },
        { field: "updDateTime", title: "Utolsó szinkron ideje", width: 110, filterable: false, cell: "updDateTime" },
      ];
    },
  },

  mounted() {
    // Ha van cache-elt tétel lista a head-en, akkor az auto-load helyett azt töltjük be —
    // így a korábban szerkesztett tételek állapota (inEdit, userModified) megmarad újranyitáskor.
    if (this.hasCachedItems) {
      this.$nextTick(() => {
        eventBus.$emit(`${this.apiSearchName}:set-items`, this.head._cachedItems);
      });
    }
  },

  methods: {
    isItemInEdit(item: any): boolean {
      return !this.readonly && item.inEdit;
    },

    taxDisplay(item: any): string {
      return taxCodeAndName(item);
    },

    /**
     * BillCity-nél az egységárat nem az interfész adja, hanem a nettóból és a mennyiségből számoljuk,
     * ezért a listán csak megjelenítjük. Más interfészen az interfész adata, tehát szerkeszthető.
     */
    isCalculatedField(field: string, item: any): boolean {
      return item.interface == Iface.BillCity && field == "currUnitPrice";
    },

    /** A számla pénzneme a fejről jön — ha nincs fej (pl. önálló használat), forintban számolunk. */
    moneyDigits(field: string): number {
      const currDigits = configuration.getCurrDigits(this.head?.invCurrency ?? "HUF");
      return (field == "currUnitPrice" ? currDigits?.geniUnitDigits : currDigits?.geniItemDigits) ?? 0;
    },

    /**
     * A tételrácson a számla pénznemében szerkesztünk, a köztes tábla viszont a HUF értéket is
     * tárolja — ezért az árfolyammal átszámolva a HUF párt is frissítjük. Árfolyam nélkül a HUF
     * érték marad (nem írjuk felül rossz adattal).
     */
    setCurrAmount(item: any, field: string, value: number) {
      item[field] = value;

      const hufFields: Record<string, string> = {
        currUnitPrice: "unitPrice",
        currNetto: "netto",
        currVat: "vat",
        currTotal: "total",
      };
      const hufField = hufFields[field];
      const exchangeRate = Number(this.head?.exchangeRate) || 0;
      if (!hufField || exchangeRate <= 0) return;

      item[hufField] = roundMoney((Number(value) || 0) * exchangeRate, "HUF", 2);
    },

    /**
     * Tétel mentése — userModified=true (kék kiemelés), inEdit lezárása.
     * Ha kapott `head` propot (top-level használat), akkor a fejet is megjelöli
     * (userModified + selected), így a checkbox is kipipálódik a fő listán.
     * TODO: backend update endpoint hívása.
     */
    saveItem(item: any) {
      item.userModified = true;
      item.inEdit = false;
      if (this.head) {
        this.$set(this.head, "userModified", true);
        this.$set(this.head, "selected", true);
      }
    },

    /**
     * Az ApiSearchPage által betöltött tétel lista cache-elése a fej-rekordon,
     * hogy a felhasználói módosítások (inEdit / userModified) megmaradjanak,
     * amikor a tétel táblát becsukjuk és újra megnyitjuk.
     */
    cacheItems(items: any[]) {
      if (this.head) {
        this.$set(this.head, "_cachedItems", items);
      }
    },
  },
});
